import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, loadSession } from './api';
import { pendingMutations, purgePrivateStorage, queueMutation, removeMutation } from './offline';
import { formatDecimalInput, formatPoints, nutritionPlanDefaults, suggestedBudget, suggestedTargets, type BudgetMode } from './points';
import type { ActivityEntry, ActivityIntensity, ActivityKind, DashboardData, FoodServing, MealEntry, MealSlot, NutritionPreset, NutritionPriority, Profile, RecentFood, WaterEntry } from './types';

type View = 'today' | 'foods' | 'help' | 'settings';
type InfoTopic = 'daily-points' | 'weeklies' | 'fuel-points';
type FoodLogResult = { message: string; entry?: MealEntry; queuedMutationID?: string };
type ToastNotice = { message: string; actionLabel?: string; action?: () => void | Promise<void> };

const slotLabels: Record<MealSlot, string> = {
  breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', snack: 'Snacks',
};

const activityLabels: Record<ActivityKind, string> = {
  walk: 'Walk', cardio: 'Cardio', strength: 'Strength', sport: 'Sport', mobility: 'Mobility', other: 'Other',
};

function localDate(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultSlot(): MealSlot {
  const hour = new Date().getHours();
  if (hour < 10) return 'breakfast';
  if (hour < 15) return 'lunch';
  if (hour < 21) return 'dinner';
  return 'snack';
}

function asNumber(value: unknown) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function Progress({ label, value, target, unit, color, onAction, incomplete = false, limit = false, children }: {
  label: string; value: number; target: number | null; unit: string; color: string; onAction?: () => void;
  incomplete?: boolean; limit?: boolean; children?: React.ReactNode;
}) {
  const percent = target && target > 0 ? Math.min(100, Math.round((Number(value) / Number(target)) * 100)) : 0;
  const content = <>
      <div className="progress-ring" style={{ '--progress': `${percent * 3.6}deg`, '--ring': color } as React.CSSProperties}>
        <span>{percent}%</span>
      </div>
      <div><strong>{label}{onAction && <span className="progress-add" aria-hidden="true">+</span>}</strong><small>{Math.round(Number(value))} / {target ? Math.round(Number(target)) : '—'} {unit}{limit ? ' limit' : ''}</small>{incomplete && <small className="data-incomplete">Some food data missing</small>}{children}</div>
    </>;
  return onAction
    ? <button className="progress-card actionable" onClick={onAction} aria-label={`Log ${label.toLowerCase()}`}>{content}</button>
    : <div className={`progress-card ${incomplete ? 'incomplete' : ''}`}>{content}</div>;
}

function QualityBadge({ food, onExplain }: { food: FoodServing; onExplain?: (topic: InfoTopic) => void }) {
  const personal = food.display_points_algorithm?.startsWith('family-fuel-points-v') === true
    && food.display_points_algorithm !== 'family-fuel-points-v1';
  if (food.display_points_quality === 'estimated') return onExplain
    ? <button type="button" className="badge estimate badge-button" onClick={() => onExplain('fuel-points')} aria-label="What are Fuel Points?">{personal ? 'My Fuel Points' : 'Fuel Points'} <span aria-hidden="true">?</span></button>
    : <span className="badge estimate" title="Family Meals nutrition estimate">{personal ? 'My Fuel Points' : 'Fuel Points'}</span>;
  if (food.display_points_quality === 'unknown') return <span className="badge unknown" title={food.display_points_missing_nutrients?.length ? `Missing ${food.display_points_missing_nutrients.join(', ')}` : undefined}>Points unknown</span>;
  return <span className="badge confirmed">Points confirmed</span>;
}

const infoTopics: Record<InfoTopic, { title: string; summary: string; detail: string }> = {
  'daily-points': {
    title: 'Daily Points',
    summary: 'Your food target for today.',
    detail: 'Logged foods subtract from it. Exercise is tracked separately and does not add food Points.',
  },
  weeklies: {
    title: 'Weeklies',
    summary: 'Flexible Points for this week.',
    detail: 'They cover days that go over your Daily Points and may include a small rollover from unused Daily Points.',
  },
  'fuel-points': {
    title: 'Fuel Points',
    summary: 'Your transparent Family Meals nutrition score.',
    detail: 'With a nutrition plan, your selected priorities gently change the score and published Points remain source detail. Missing required nutrition stays unknown. This is not WeightWatchers’ proprietary formula.',
  },
};

function InfoSheet({ topic, onClose, onReadMore }: { topic: InfoTopic; onClose: () => void; onReadMore: () => void }) {
  const info = infoTopics[topic];
  return <div className="sheet-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <section className="add-sheet info-sheet" role="dialog" aria-modal="true" aria-labelledby="info-sheet-title">
      <div className="sheet-grabber" />
      <header><div><p className="eyebrow">What this means</p><h2 id="info-sheet-title">{info.title}</h2></div><button className="icon-button" onClick={onClose} aria-label="Close">×</button></header>
      <strong>{info.summary}</strong><p>{info.detail}</p>
      <button type="button" className="secondary-button" onClick={onReadMore}>Read the full guide</button>
    </section>
  </div>;
}

function FoodRow({ food, onAdd, busy }: { food: FoodServing; onAdd: (food: FoodServing) => void; busy?: boolean }) {
  return (
    <button className="food-row" onClick={() => onAdd(food)} disabled={busy}>
      <span className="food-copy">
        <strong>{food.name}</strong>
        <small>{[food.brand || food.restaurant_name, `${food.amount ?? 1} ${food.unit || food.label}`].filter(Boolean).join(' · ')}</small>
        <QualityBadge food={food} />
      </span>
      <span className="food-metrics">
        <b>{formatPoints(food.display_points)}</b><small>pts</small>
        <span className="add-mark" aria-hidden="true">+</span>
      </span>
    </button>
  );
}

function Login() {
  return (
    <main className="login-shell">
      <div className="brand-mark">FM</div>
      <p className="eyebrow">A calmer way to keep track</p>
      <h1>Family Meals</h1>
      <p>Quick meal logging, useful nutrition context, and private progress for your family.</p>
      <a className="primary-button google-button" href="/auth/google/login">
        <span aria-hidden="true">G</span> Continue with Google
      </a>
      <small>Only invited family email addresses can sign in.</small>
    </main>
  );
}

function Onboarding({ profile, onDone }: { profile: Profile; onDone: () => void }) {
  const [weightLb, setWeightLb] = useState(profile.current_weight_kg ? String(Math.round(Number(profile.current_weight_kg) * 2.20462)) : '');
  const [heightInches, setHeightInches] = useState(profile.height_cm ? String(Math.round(Number(profile.height_cm) / 2.54)) : '');
  const [ageYears, setAgeYears] = useState(profile.age_years?.toString() ?? '');
  const [sex, setSex] = useState<'female' | 'male' | 'unspecified'>(profile.sex_for_energy ?? 'unspecified');
  const [baselineActivity, setBaselineActivity] = useState<'mostly_seated' | 'some_movement' | 'active_day'>(profile.baseline_activity ?? 'mostly_seated');
  const [dose, setDose] = useState(formatDecimalInput(profile.medication_dose_mg));
  const [fiber, setFiber] = useState('25');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const weightKg = asNumber(weightLb) ? Number(weightLb) / 2.20462 : null;
  const heightCm = asNumber(heightInches) ? Number(heightInches) * 2.54 : null;
  const suggestions = suggestedTargets(weightKg, Number(fiber));
  const protein = suggestions.proteinG;
  const budget = suggestedBudget({
    weightKg, heightCm, ageYears: asNumber(ageYears), sex, baselineActivity,
    proteinG: protein, fiberG: Number(fiber),
  });

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true); setError('');
    try {
      await api('/api/v1/profile', { method: 'PUT', body: JSON.stringify({
        current_weight_kg: weightKg,
        starting_weight_kg: profile.starting_weight_kg ?? weightKg,
        height_cm: heightCm,
        age_years: asNumber(ageYears),
        sex_for_energy: sex,
        baseline_activity: baselineActivity,
        points_budget_source: 'family_suggestion_v1',
        medication_name: 'Wegovy',
        medication_dose_mg: asNumber(dose),
        daily_points_budget: budget?.dailyPoints,
        weekly_points_budget: budget?.weeklyPoints,
        onboarding_complete: true,
      }) });
      await api('/api/v1/targets', { method: 'POST', body: JSON.stringify({
        protein_g: protein,
        fiber_g: Number(fiber),
        water_ml: 1892.71,
        source: 'confirmed_suggestion',
      }) });
      const targetCalories = budget?.targetCalories ?? 2000;
      await api('/api/v1/nutrition-plan', { method: 'PUT', body: JSON.stringify({
        preset_key: 'balanced', priorities: [], protein_target_g: protein, fiber_target_g: Number(fiber),
        water_target_ml: 1892.71, saturated_fat_limit_g: targetCalories * 0.1 / 9,
        added_sugar_limit_g: targetCalories * 0.05 / 4, sodium_limit_mg: 2300,
        protein_per_meal_g: null, daily_points_budget: budget?.dailyPoints,
        weekly_points_budget: budget?.weeklyPoints, budget_source: 'family_suggestion_v2', activate_today: true,
      }) });
      onDone();
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Could not save'); }
    finally { setSaving(false); }
  }

  return (
    <main className="onboarding">
      <p className="eyebrow">Private setup · about a minute</p>
      <h1>Make this yours</h1>
      <p className="lede">Tell us what you know; Family Meals will calculate the starting guidance. These private suggestions stay editable.</p>
      <form onSubmit={submit} className="form-stack">
        <label>Current weight <span><input inputMode="decimal" value={weightLb} onChange={(e) => setWeightLb(e.target.value)} required /> lb</span></label>
        <div className="two-fields">
          <label>Height <span><input inputMode="numeric" value={heightInches} onChange={(e) => setHeightInches(e.target.value)} required /> in</span></label>
          <label>Age <span><input inputMode="numeric" min="18" max="120" value={ageYears} onChange={(e) => setAgeYears(e.target.value)} required /> years</span></label>
        </div>
        <fieldset><legend>Sex used for the energy estimate</legend><div className="choice-row">
          {([['female', 'Female'], ['male', 'Male'], ['unspecified', 'Prefer not to say']] as const).map(([value, label]) => <button className={sex === value ? 'selected' : ''} type="button" onClick={() => setSex(value)} key={value}>{label}</button>)}
        </div></fieldset>
        <fieldset><legend>What is a typical day like?</legend><div className="activity-levels">
          {([['mostly_seated', 'Mostly seated'], ['some_movement', 'Some movement'], ['active_day', 'Active most of the day']] as const).map(([value, label]) => <button className={baselineActivity === value ? 'selected' : ''} type="button" onClick={() => setBaselineActivity(value)} key={value}>{label}</button>)}
        </div><small className="field-help">Choose your usual baseline. Logged workouts do not add food Points.</small></fieldset>
        <label>Current Wegovy dose <span><input inputMode="decimal" value={dose} onChange={(e) => setDose(e.target.value)} /> mg</span></label>
        <fieldset>
          <legend>Fiber reference</legend>
          <div className="choice-row">
            {[25, 38].map((choice) => <button className={fiber === String(choice) ? 'selected' : ''} type="button" onClick={() => setFiber(String(choice))} key={choice}>{choice} g</button>)}
          </div>
        </fieldset>
        <div className="suggestion">
          <span>Your suggested starting guidance</span>
          <strong>{budget ? `${budget.dailyPoints} daily · ${budget.weeklyPoints} flexible weekly Points` : 'Add weight, height, and age to calculate'}</strong>
          <small>{protein || '—'} g protein · {fiber} g fiber · 64 fl oz water. The budget is an estimate based on your profile and can be tuned in Settings.</small>
        </div>
        {error && <p className="error" role="alert">{error}</p>}
        <button className="primary-button" disabled={saving || !budget}>{saving ? 'Saving…' : 'Use this starting guidance'}</button>
      </form>
    </main>
  );
}

function AddSheet({ dashboard, initialSlot, onClose, onLogged }: {
  dashboard?: DashboardData; initialSlot: MealSlot; onClose: () => void; onLogged: (result: FoodLogResult) => void;
}) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [slot, setSlot] = useState<MealSlot>(initialSlot);
  const [busy, setBusy] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const foods = useQuery({
    queryKey: ['foods', query],
    queryFn: () => api<FoodServing[]>(`/api/v1/foods?q=${encodeURIComponent(query)}`),
    enabled: query.trim().length > 0,
  });

  useEffect(() => { searchRef.current?.focus(); }, []);

  async function add(food: FoodServing) {
    const mutationID = crypto.randomUUID();
    const body = {
      serving_id: food.serving_id,
      client_mutation_id: mutationID,
      eaten_at: new Date().toISOString(),
      local_date: localDate(),
      slot,
      quantity: 1,
    };
    setBusy(food.serving_id);
    try {
      const entry = await api<MealEntry>('/api/v1/entries', { method: 'POST', body: JSON.stringify(body) });
      onLogged({ message: `${food.name} added to ${slotLabels[slot]}`, entry });
    } catch (reason) {
      const networkFailure = !navigator.onLine || (reason instanceof TypeError) || String(reason).includes('Failed to fetch');
      if (!networkFailure) { onLogged({ message: reason instanceof Error ? reason.message : 'Could not add food' }); setBusy(''); return; }
      await queueMutation({ id: mutationID, path: '/api/v1/entries', body, queuedAt: new Date().toISOString() });
      onLogged({ message: `${food.name} saved offline for ${slotLabels[slot]}`, queuedMutationID: mutationID });
    }
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    await queryClient.invalidateQueries({ queryKey: ['day'] });
    await queryClient.invalidateQueries({ queryKey: ['food-catalog'] });
    onClose();
  }

  const recents = useMemo(() => dashboard?.recent.map((recent) => ({ ...recent.snapshot, serving_id: recent.serving_id })) ?? [], [dashboard]);

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section className="add-sheet" role="dialog" aria-modal="true" aria-label="Add food">
        <div className="sheet-grabber" />
        <header><h2>Add food</h2><button className="icon-button" onClick={onClose} aria-label="Close">×</button></header>
        <div className="slot-row" aria-label="Meal">
          {(Object.keys(slotLabels) as MealSlot[]).map((key) => <button className={slot === key ? 'active' : ''} key={key} onClick={() => setSlot(key)}>{slotLabels[key]}</button>)}
        </div>
        {!showCreate && <>
          <label className="search-box"><span aria-hidden="true">⌕</span><input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search foods and restaurants" /></label>
          <div className="sheet-results">
            {!query && <><h3>Recent</h3>{recents.length ? recents.map((food) => <FoodRow key={food.serving_id} food={food} onAdd={add} busy={busy === food.serving_id} />) : <p className="empty">Your recently logged foods will appear here.</p>}</>}
            {query && foods.isLoading && <p className="empty">Searching…</p>}
            {query && foods.data?.map((food) => <FoodRow key={food.serving_id} food={food} onAdd={add} busy={busy === food.serving_id} />)}
            {query && foods.data?.length === 0 && <p className="empty">No match yet.</p>}
          </div>
        </>}
        <button type="button" className="secondary-button create-toggle" onClick={() => setShowCreate(!showCreate)}>{showCreate ? '← Back to search' : '+ Create a new food'}</button>
        {showCreate && <FoodForm initialName={query} submitLabel={`Save and add to ${slotLabels[slot]}`} onSaved={(food) => void add(food)} />}
      </section>
    </div>
  );
}

function FoodForm({ initialName = '', food, submitLabel, onSaved }: {
  initialName?: string; food?: FoodServing; submitLabel: string; onSaved: (food: FoodServing) => void;
}) {
  const value = (input: number | string | null | undefined) => input == null ? '' : String(input);
  const [name, setName] = useState(food?.name ?? initialName);
  const [servingLabel, setServingLabel] = useState(food?.label ?? '1 serving');
  const [amount, setAmount] = useState(value(food?.amount ?? 1));
  const [unit, setUnit] = useState(food?.unit ?? 'serving');
  const [brand, setBrand] = useState(food?.brand ?? '');
  const [restaurant, setRestaurant] = useState(food?.restaurant_name ?? '');
  const [calories, setCalories] = useState(value(food?.calories));
  const [protein, setProtein] = useState(value(food?.protein_g));
  const [fiber, setFiber] = useState(value(food?.fiber_g));
  const [totalFat, setTotalFat] = useState(value(food?.total_fat_g));
  const [satFat, setSatFat] = useState(value(food?.saturated_fat_g));
  const [carbs, setCarbs] = useState(value(food?.carbohydrate_g));
  const [sugar, setSugar] = useState(value(food?.sugar_g));
  const [addedSugar, setAddedSugar] = useState(value(food?.added_sugar_g));
  const [sodium, setSodium] = useState(value(food?.sodium_mg));
  const [points, setPoints] = useState(value(food?.confirmed_points));
  const [sourceURL, setSourceURL] = useState(food?.source_url ?? '');
  const [sourceDate, setSourceDate] = useState(food?.source_retrieved_on ?? '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault(); setError('');
    const amountValue = asNumber(amount);
    if (!name.trim() || !servingLabel.trim() || !unit.trim() || amountValue === null || amountValue <= 0) {
      setError('Enter a food name and a valid serving.'); return;
    }
    setSaving(true);
    try {
      const saved = await api<FoodServing>(food ? `/api/v1/foods/${food.food_id}` : '/api/v1/foods', { method: food ? 'PUT' : 'POST', body: JSON.stringify({
        name, serving_label: servingLabel, amount: amountValue, unit, brand, restaurant_name: restaurant,
        calories: asNumber(calories), protein_g: asNumber(protein), fiber_g: asNumber(fiber),
        total_fat_g: asNumber(totalFat), saturated_fat_g: asNumber(satFat),
        carbohydrate_g: asNumber(carbs), sugar_g: asNumber(sugar), added_sugar_g: asNumber(addedSugar),
        sodium_mg: asNumber(sodium), confirmed_points: asNumber(points),
        source_url: sourceURL || null, source_retrieved_on: sourceDate || null,
      }) });
      onSaved(saved);
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Could not save food'); }
    finally { setSaving(false); }
  }
  return <form className="create-food" onSubmit={submit}>
    <label>Food name<input value={name} onChange={(e) => setName(e.target.value)} required /></label>
    <fieldset className="serving-fields"><legend>Serving</legend>
      <label>Description<input value={servingLabel} onChange={(e) => setServingLabel(e.target.value)} placeholder="1 slice, 6 oz, or 1 bowl" required /></label>
    </fieldset>
    <fieldset className="nutrition-priority"><legend>Helpful nutrition</legend><small>These fields support personal Fuel Points and daily focus cards.</small><div className="nutrient-grid priority-grid">
      <label>Calories<input inputMode="decimal" min="0" value={calories} onChange={(e) => setCalories(e.target.value)} /></label>
      <label>Protein g<input inputMode="decimal" min="0" value={protein} onChange={(e) => setProtein(e.target.value)} /></label>
      <label>Fiber g<input inputMode="decimal" min="0" value={fiber} onChange={(e) => setFiber(e.target.value)} /></label>
      <label>Sat. fat g<input inputMode="decimal" min="0" value={satFat} onChange={(e) => setSatFat(e.target.value)} /></label>
      <label>Sodium mg<input inputMode="decimal" min="0" value={sodium} onChange={(e) => setSodium(e.target.value)} /></label>
    </div></fieldset>
    <details className="food-more"><summary>More details</summary><div className="two-fields optional-identity">
      <label>Serving amount<input inputMode="decimal" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required /></label>
      <label>Serving unit<input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="slice" required /></label>
      <label>Brand <small>Optional</small><input value={brand} onChange={(e) => setBrand(e.target.value)} /></label>
      <label>Restaurant <small>Optional</small><input value={restaurant} onChange={(e) => setRestaurant(e.target.value)} /></label>
    </div><div className="nutrient-grid more-nutrients">
      <label>Total fat g<input inputMode="decimal" min="0" value={totalFat} onChange={(e) => setTotalFat(e.target.value)} /></label>
      <label>Carbs g<input inputMode="decimal" min="0" value={carbs} onChange={(e) => setCarbs(e.target.value)} /></label>
      <label>Total sugar g<input inputMode="decimal" min="0" value={sugar} onChange={(e) => setSugar(e.target.value)} /></label>
      <label>Added sugar g<input inputMode="decimal" min="0" value={addedSugar} onChange={(e) => setAddedSugar(e.target.value)} /></label>
      <label>Published Points<input inputMode="numeric" min="0" step="1" value={points} onChange={(e) => setPoints(e.target.value)} /></label>
      <label>Source URL<input inputMode="url" value={sourceURL} onChange={(e) => setSourceURL(e.target.value)} /></label>
      <label>Source date<input type="date" value={sourceDate} onChange={(e) => setSourceDate(e.target.value)} /></label>
    </div></details>
    <small>Published Points are retained as source detail. Personal Fuel Points use the nutrition above.</small>
    {error && <p className="error create-food-error" role="alert">{error}</p>}
    <button className="primary-button" type="submit" disabled={saving}>{saving ? 'Saving…' : submitLabel}</button>
  </form>;
}

function FoodEditor({ food, onClose, onSaved }: { food: FoodServing; onClose: () => void; onSaved: (food: FoodServing) => void }) {
  return <div className="sheet-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <section className="add-sheet food-editor" role="dialog" aria-modal="true" aria-label={`Edit ${food.name}`}>
      <div className="sheet-grabber" />
      <header><div><p className="eyebrow">Your household food</p><h2>Edit food</h2></div><button className="icon-button" onClick={onClose} aria-label="Close">×</button></header>
      <FoodForm food={food} submitLabel="Save food changes" onSaved={onSaved} />
    </section>
  </div>;
}

function WaterSheet({ onClose, onSave }: { onClose: () => void; onSave: (amountML: number) => Promise<void> }) {
  const [ounces, setOunces] = useState('12');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  async function save() {
    const amount = asNumber(ounces);
    if (amount === null || amount <= 0 || amount > 128) { setError('Enter an amount between 1 and 128 fl oz.'); return; }
    setSaving(true); setError('');
    try { await onSave(amount * 29.5735); }
    catch (reason) { setError(reason instanceof Error ? reason.message : 'Could not log water'); setSaving(false); }
  }
  return <div className="sheet-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <section className="add-sheet water-sheet" role="dialog" aria-modal="true" aria-label="Log water">
      <div className="sheet-grabber" />
      <header><div><p className="eyebrow">Hydration</p><h2>Log water</h2></div><button className="icon-button" onClick={onClose} aria-label="Close">×</button></header>
      <div className="water-choices" role="group" aria-label="Common water amounts">{[8, 12, 16, 24, 32].map((amount) => <button type="button" className={ounces === String(amount) ? 'selected' : ''} onClick={() => setOunces(String(amount))} key={amount}>{amount} oz</button>)}</div>
      <label>Custom amount <span><input inputMode="decimal" min="1" max="128" value={ounces} onChange={(event) => setOunces(event.target.value)} /><b>fl oz</b></span></label>
      {error && <p className="error" role="alert">{error}</p>}
      <button className="primary-button" onClick={save} disabled={saving}>{saving ? 'Logging…' : `Log ${formatDecimalInput(asNumber(ounces), 1) || 'water'} fl oz`}</button>
    </section>
  </div>;
}

function ActivitySheet({ onClose, onLogged }: { onClose: () => void; onLogged: (message: string) => void }) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<ActivityKind>('walk');
  const [duration, setDuration] = useState(30);
  const [intensity, setIntensity] = useState<ActivityIntensity>('moderate');
  const [saving, setSaving] = useState(false);

  async function save() {
    const mutationID = crypto.randomUUID();
    const body = {
      client_mutation_id: mutationID,
      started_at: new Date().toISOString(),
      local_date: localDate(),
      kind, duration_minutes: duration, intensity,
    };
    setSaving(true);
    try {
      await api('/api/v1/activities', { method: 'POST', body: JSON.stringify(body) });
      onLogged(`${activityLabels[kind]} logged`);
    } catch (reason) {
      const networkFailure = !navigator.onLine || reason instanceof TypeError || String(reason).includes('Failed to fetch');
      if (!networkFailure) { onLogged(reason instanceof Error ? reason.message : 'Could not log activity'); setSaving(false); return; }
      await queueMutation({ id: mutationID, path: '/api/v1/activities', body, queuedAt: new Date().toISOString() });
      onLogged(`${activityLabels[kind]} saved offline`);
    }
    await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    await queryClient.invalidateQueries({ queryKey: ['activities'] });
    onClose();
  }

  return <div className="sheet-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
    <section className="add-sheet activity-sheet" role="dialog" aria-modal="true" aria-label="Log movement">
      <div className="sheet-grabber" />
      <header><div><p className="eyebrow">Points stay unchanged</p><h2>Log movement</h2></div><button className="icon-button" onClick={onClose} aria-label="Close">×</button></header>
      <fieldset><legend>What did you do?</legend><div className="activity-choices">
        {(Object.keys(activityLabels) as ActivityKind[]).map((choice) => <button type="button" className={kind === choice ? 'selected' : ''} onClick={() => setKind(choice)} key={choice}>{activityLabels[choice]}</button>)}
      </div></fieldset>
      <fieldset><legend>Minutes</legend><div className="choice-row duration-row">
        {[15, 30, 45, 60].map((choice) => <button type="button" className={duration === choice ? 'selected' : ''} onClick={() => setDuration(choice)} key={choice}>{choice}</button>)}
      </div></fieldset>
      <fieldset><legend>Effort</legend><div className="choice-row">
        {(['light', 'moderate', 'vigorous'] as ActivityIntensity[]).map((choice) => <button type="button" className={intensity === choice ? 'selected' : ''} onClick={() => setIntensity(choice)} key={choice}>{choice[0].toUpperCase() + choice.slice(1)}</button>)}
      </div></fieldset>
      <p className="activity-policy">Movement supports health and muscle retention. Family Meals does not convert estimated calories burned into food Points.</p>
      <button className="primary-button" onClick={save} disabled={saving}>{saving ? 'Saving…' : `Log ${duration}-minute ${activityLabels[kind].toLowerCase()}`}</button>
    </section>
  </div>;
}

function EntryEditor({ entry, onClose, onSave, onRemove }: {
  entry: MealEntry; onClose: () => void;
  onSave: (slot: MealSlot, quantity: number) => Promise<void>;
  onRemove: () => Promise<void>;
}) {
  const [slot, setSlot] = useState<MealSlot>(entry.slot);
  const [quantity, setQuantity] = useState(formatDecimalInput(entry.quantity, 2));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function save(event: FormEvent) {
    event.preventDefault(); setError('');
    const nextQuantity = asNumber(quantity);
    if (nextQuantity === null || nextQuantity <= 0) { setError('Enter a quantity greater than zero.'); return; }
    setSaving(true);
    try { await onSave(slot, nextQuantity); }
    catch (reason) { setError(reason instanceof Error ? reason.message : 'Could not update entry'); setSaving(false); }
  }

  async function remove() {
    setSaving(true); setError('');
    try { await onRemove(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : 'Could not remove entry'); setSaving(false); }
  }

  return <div className="sheet-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <section className="add-sheet entry-editor" role="dialog" aria-modal="true" aria-label={`Edit ${entry.snapshot.name}`}>
      <div className="sheet-grabber" />
      <header><div><p className="eyebrow">Meal entry</p><h2>{entry.snapshot.name}</h2></div><button className="icon-button" onClick={onClose} aria-label="Close">×</button></header>
      <form onSubmit={save} className="form-stack">
        <fieldset><legend>Meal</legend><div className="slot-row editor-slots">
          {(Object.keys(slotLabels) as MealSlot[]).map((choice) => <button type="button" className={slot === choice ? 'active' : ''} key={choice} onClick={() => setSlot(choice)}>{slotLabels[choice]}</button>)}
        </div></fieldset>
        <label>Quantity <small>{entry.snapshot.label}</small><input inputMode="decimal" min="0.01" step="0.25" value={quantity} onChange={(event) => setQuantity(event.target.value)} required /></label>
        {error && <p className="error" role="alert">{error}</p>}
        <button className="primary-button" disabled={saving}>{saving ? 'Saving…' : `Save in ${slotLabels[slot]}`}</button>
        <button className="text-button remove-entry" type="button" onClick={remove} disabled={saving}>Remove from today</button>
      </form>
    </section>
  </div>;
}

function Today({ dashboard, entries, activities, onAdd, onQuickAdd, quickBusy, onEdit, onDelete, onAddWater, onAddActivity, onDeleteActivity, onExplain, loading }: {
  dashboard?: DashboardData; entries?: MealEntry[]; activities?: ActivityEntry[]; onAdd: (slot?: MealSlot) => void;
  onQuickAdd: (item: RecentFood) => void; quickBusy: string; onEdit: (entry: MealEntry) => void;
  onDelete: (entry: MealEntry) => void; onAddWater: () => void; onAddActivity: () => void; onDeleteActivity: (id: string) => void;
  onExplain: (topic: InfoTopic) => void; loading: boolean;
}) {
  if (loading || !dashboard) return <div className="page-loading">Preparing today…</div>;
  const s = dashboard.summary;
  const focusPriorities: NutritionPriority[] = Array.from(new Set<NutritionPriority>([
    'protein', 'fiber', ...(s.nutrition_plan?.priorities ?? []),
  ]));
  const nourishmentGoals = [
    s.protein_target_g != null && Number(s.protein_g) >= Number(s.protein_target_g),
    s.fiber_target_g != null && Number(s.fiber_g) >= Number(s.fiber_target_g),
    s.water_target_ml != null && Number(s.water_ml) >= Number(s.water_target_ml),
  ].filter(Boolean).length;
  const pointsMessage = s.daily_points_remaining === null
    ? 'Set up your Points guidance in Settings'
    : Number(s.daily_points_remaining) < 0
      ? `${formatPoints(Math.abs(Number(s.daily_points_remaining)))} from Weeklies`
      : s.daily_points_budget != null && Number(s.points_consumed) >= Number(s.daily_points_budget) * 0.8
        ? "In today's target range"
        : `${formatPoints(Number(s.daily_points_remaining))} available in today's guide`;
  const focusCards: Record<NutritionPriority, React.ReactNode> = {
    protein: <Progress key="protein" label="Protein" value={s.protein_g} target={s.protein_target_g} unit="g" color="#e8875d" incomplete={Number(s.missing_protein_count) > 0}>{s.protein_by_meal?.target && <span className="meal-targets">{(['breakfast','lunch','dinner'] as const).map((meal) => <span className={Number(s.protein_by_meal[meal]) >= Number(s.protein_by_meal.target) ? 'met' : ''} key={meal}>{meal[0].toUpperCase()}</span>)}</span>}</Progress>,
    fiber: <Progress key="fiber" label="Fiber" value={s.fiber_g} target={s.fiber_target_g} unit="g" color="#78a85a" incomplete={Number(s.missing_fiber_count) > 0} />,
    saturated_fat: <Progress key="saturated-fat" label="Sat. fat" value={s.saturated_fat_g} target={s.saturated_fat_limit_g} unit="g" color="#b97858" limit incomplete={Number(s.missing_saturated_fat_count) > 0} />,
    added_sugar: <Progress key="added-sugar" label="Added sugar" value={s.added_sugar_g} target={s.added_sugar_limit_g} unit="g" color="#b97858" limit incomplete={Number(s.missing_added_sugar_count) > 0} />,
    sodium: <Progress key="sodium" label="Sodium" value={s.sodium_mg} target={s.sodium_limit_mg} unit="mg" color="#b97858" limit incomplete={Number(s.missing_sodium_count) > 0} />,
  };
  return <main className="page today-page">
    <header className="today-header"><div><p className="eyebrow">Today</p><h1>Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}</h1></div><button className="avatar" aria-label="Account">FM</button></header>
    <section className="points-card">
      <div><button type="button" className="term-trigger" onClick={() => onExplain('daily-points')}>Daily Points <span aria-hidden="true">?</span></button><strong>{formatPoints(s.points_consumed)}<small> / {s.daily_points_budget ?? '—'}</small></strong><p>{pointsMessage}</p></div>
      <div className="weekly"><button type="button" className="term-trigger" onClick={() => onExplain('weeklies')}>Weeklies <span aria-hidden="true">?</span></button><b>{formatPoints(s.weekly_points_remaining)}</b><small>remaining</small></div>
    </section>
    {(Number(s.estimated_points_count) > 0 || Number(s.unknown_points_count) > 0) && <p className="quality-note">
      {Number(s.estimated_points_count) > 0 && <>{Number(s.estimated_points_count)} food{Number(s.estimated_points_count) === 1 ? '' : 's'} use{Number(s.estimated_points_count) === 1 ? 's' : ''} estimated <button type="button" onClick={() => onExplain('fuel-points')}>Fuel Points</button>.</>}
      {Number(s.estimated_points_count) > 0 && Number(s.unknown_points_count) > 0 && ' '}
      {Number(s.unknown_points_count) > 0 && <>{Number(s.unknown_points_count)} food{Number(s.unknown_points_count) === 1 ? '' : 's'} ha{Number(s.unknown_points_count) === 1 ? 's' : 've'} unknown Points.</>}
    </p>}
    <section className="focus-section" aria-label="Nourishment"><div className="focus-heading"><h2>Nourishment</h2><p>{nourishmentGoals} of 3 daily goals reached</p></div><div className="progress-grid">
      {focusPriorities.map((priority) => focusCards[priority])}
      <Progress label="Water" value={Number(s.water_ml) / 29.5735} target={s.water_target_ml ? Number(s.water_target_ml) / 29.5735 : null} unit="fl oz" color="#5e9caf" onAction={onAddWater} />
    </div></section>
    {dashboard.recent.length > 0 && <section className="quick-section"><div className="section-title"><h2>Quick add</h2><button onClick={() => onAdd()}>See all</button></div><div className="quick-scroll">{dashboard.recent.slice(0, 6).map((item) => <button key={item.serving_id} onClick={() => onQuickAdd(item)} disabled={quickBusy === item.serving_id} aria-label={`Add ${item.snapshot.name} to ${slotLabels[item.slot]}`}><span>+</span><strong>{item.snapshot.name}</strong><small>{slotLabels[item.slot]} · {formatPoints(item.snapshot.display_points)} pts</small></button>)}</div></section>}
    <section className="meals-section">
      <div className="section-title"><h2>Meals</h2><button onClick={() => onAdd()}>+ Add</button></div>
      {(Object.keys(slotLabels) as MealSlot[]).map((slot) => {
        const group = entries?.filter((entry) => entry.slot === slot) ?? [];
        const points = group.reduce((sum, entry) => sum + Number(entry.snapshot.display_points ?? 0) * Number(entry.quantity), 0);
        return <div className="meal-group" key={slot}>
          <div className="meal-heading">
            <h3>{slotLabels[slot]}</h3>
            <div className="meal-heading-actions"><span>{formatPoints(points)} pts</span><button type="button" onClick={() => onAdd(slot)} aria-label={`Add food to ${slotLabels[slot]}`}>+ Add</button></div>
          </div>
          {group.length === 0 ? <p className="empty-meal">Nothing logged yet</p> : group.map((entry) => <div className="entry-row" key={entry.id}><button className="entry-edit" onClick={() => onEdit(entry)} aria-label={`Edit ${entry.snapshot.name} in ${slotLabels[entry.slot]}`}><strong>{entry.snapshot.name}</strong><small>{entry.quantity !== 1 ? `${formatDecimalInput(entry.quantity, 2)} × ` : ''}{entry.snapshot.label}</small></button><span>{formatPoints(entry.snapshot.display_points == null ? null : Number(entry.snapshot.display_points) * Number(entry.quantity))} pts</span><button className="entry-remove" onClick={() => onDelete(entry)} aria-label={`Remove ${entry.snapshot.name}`}>×</button></div>)}
        </div>;
      })}
    </section>
    <section className="movement-section">
      <div className="movement-heading"><h2>Movement</h2><button onClick={onAddActivity}>+ Log movement</button></div>
      <div className="movement-summary" aria-label="Weekly movement progress">
        <span><strong>{Number(dashboard.activity?.week_minutes ?? 0)}</strong> / 150 min</span>
        <span><strong>{Number(dashboard.activity?.strength_days ?? 0)}</strong> / 2 strength days</span>
      </div>
      <details className="movement-details" open={Number(dashboard.activity?.recovery_sessions ?? 0) > 0 || Number(dashboard.activity?.today_sessions ?? 0) > 0}>
        <summary>{Number(dashboard.activity?.today_sessions ?? 0) > 0 ? `${Number(dashboard.activity?.today_sessions)} logged today` : 'How movement affects food'}</summary>
        <div className="movement-detail-body">
          {(activities ?? []).map((activity) => <div className="activity-row" key={activity.id}><div><strong>{activityLabels[activity.kind]}</strong><small>{activity.duration_minutes} min · {activity.intensity}</small></div><button onClick={() => onDeleteActivity(activity.id)} aria-label={`Remove ${activityLabels[activity.kind]}`}>×</button></div>)}
          {Number(dashboard.activity?.recovery_sessions ?? 0) > 0
            ? <div className="recovery-note"><strong>Recovery matters today</strong><p>Your Points budget has not changed. Aim to meet your usual protein target and drink extra fluid; consider electrolytes after long, hot, or very sweaty activity.</p></div>
            : <p className="movement-note">Exercise is tracked separately and never automatically adds food Points.</p>}
        </div>
      </details>
    </section>
  </main>;
}

function CatalogFood({ food, meal, onLog, onFavorite, onEdit, onExplain, busy }: {
  food: FoodServing; meal: string; onLog: () => void; onFavorite: () => void; onEdit: () => void; onExplain: (topic: InfoTopic) => void; busy?: boolean;
}) {
  return <article className="catalog-food">
    <div className="catalog-food-title"><div><strong>{food.name}</strong><small>{[food.restaurant_name || food.brand, food.label].filter(Boolean).join(' · ')}</small></div><div className="catalog-points"><b>{formatPoints(food.display_points)}</b><span>pts</span></div></div>
    <div className="catalog-meta"><QualityBadge food={food} onExplain={onExplain} />{food.display_points_missing_nutrients?.length ? <span>Missing {food.display_points_missing_nutrients.map((item) => item.replaceAll('_',' ')).join(', ')}</span> : null}{food.display_points_algorithm?.startsWith('family-fuel-points-v') && food.display_points_algorithm !== 'family-fuel-points-v1' && food.confirmed_points != null && <span>Published: {food.confirmed_points} pts</span>}<span>{food.nutrient_quality === 'official' ? 'Official nutrition' : food.editable ? 'Created by you' : 'Household entry'}</span>{food.source_retrieved_on && <span>· {food.source_retrieved_on}</span>}{food.source_url && <a href={food.source_url} target="_blank" rel="noreferrer">Source</a>}</div>
    {food.display_points_breakdown && <details className="score-breakdown"><summary>Why this score?</summary><dl>{Object.entries(food.display_points_breakdown).filter(([,value]) => Number(value) !== 0).map(([key,value]) => <div key={key}><dt>{key === 'base' ? 'Balanced base' : key.replaceAll('_',' ')}</dt><dd>{Number(value) > 0 && key !== 'base' ? '+' : ''}{formatDecimalInput(Number(value), 2)}</dd></div>)}</dl><small>Priority adjustments are normalized to your daily goals and limits. Version 3 keeps scores to one decimal and totals them without per-food whole-number rounding.</small></details>}
    <div className="catalog-actions">
      <button onClick={onFavorite} aria-label={`${food.favorite ? 'Remove' : 'Add'} ${food.name} ${food.favorite ? 'from' : 'to'} favorites`}>{food.favorite ? '★ Favorite' : '☆ Favorite'}</button>
      {food.editable && <button onClick={onEdit}>Edit</button>}
      <button className="catalog-log" onClick={onLog} disabled={busy}>{busy ? 'Adding…' : `+ ${meal}`}</button>
    </div>
  </article>;
}

function FoodCatalog({ onLog, onCreate, onEdit, onExplain }: {
  onLog: (food: FoodServing) => Promise<void>; onCreate: () => void; onEdit: (food: FoodServing) => void; onExplain: (topic: InfoTopic) => void;
}) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState('');
  const foods = useQuery({
    queryKey: ['food-catalog', query],
    queryFn: () => api<FoodServing[]>(`/api/v1/foods?q=${encodeURIComponent(query)}&limit=100`),
  });
  const meal = slotLabels[defaultSlot()];
  async function toggle(food: FoodServing) {
    await api('/api/v1/favorites', { method: 'POST', body: JSON.stringify({ serving_id: food.serving_id, favorite: !food.favorite }) });
    await Promise.all([queryClient.invalidateQueries({ queryKey: ['food-catalog'] }), queryClient.invalidateQueries({ queryKey: ['foods'] }), queryClient.invalidateQueries({ queryKey: ['dashboard'] })]);
  }
  async function log(food: FoodServing) {
    if (busy) return;
    setBusy(food.serving_id);
    try { await onLog(food); }
    finally { setBusy(''); }
  }
  const all = foods.data ?? [];
  const sections = query.trim() ? [['Search results', all] as const] : [
    ['Favorites', all.filter((food) => food.favorite)],
    ['Recent', all.filter((food) => Number(food.use_count) > 0)],
    ['Household foods', all.filter((food) => food.household)],
    ['Restaurants', all.filter((food) => food.restaurant_name)],
    ['Browse all', all],
  ] as const;
  return <main className="page foods-page">
    <p className="eyebrow">Shared household catalog</p><div className="foods-heading"><h1>Foods</h1><button onClick={onCreate}>+ Create</button></div>
    <label className="search-box catalog-search"><span aria-hidden="true">⌕</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search foods, brands, or restaurants" /></label>
    {foods.isLoading && <p className="empty">Loading foods…</p>}
    {foods.isError && <p className="error">Could not load the food catalog.</p>}
    {!foods.isLoading && sections.map(([title, items]) => items.length > 0 && (title === 'Browse all'
      ? <details className="catalog-section catalog-all" key={title}><summary><h2>{title}</h2><span>{items.length} foods</span></summary><div className="catalog-list">{items.slice(0, 50).map((food) => <CatalogFood key={`${title}-${food.serving_id}`} food={food} meal={meal} busy={busy === food.serving_id} onLog={() => void log(food)} onFavorite={() => void toggle(food)} onEdit={() => onEdit(food)} onExplain={onExplain} />)}</div></details>
      : <section className="catalog-section" key={title}><h2>{title}</h2><div className={`catalog-list ${query ? '' : 'catalog-scroll'}`}>{items.slice(0, query ? 100 : 12).map((food) => <CatalogFood key={`${title}-${food.serving_id}`} food={food} meal={meal} busy={busy === food.serving_id} onLog={() => void log(food)} onFavorite={() => void toggle(food)} onEdit={() => onEdit(food)} onExplain={onExplain} />)}</div></section>))}
    {!foods.isLoading && all.length === 0 && <div className="catalog-empty"><h2>No foods found</h2><p>Try another search or create the food for your household.</p><button className="primary-button" onClick={onCreate}>Create and log a food</button></div>}
  </main>;
}

function Help() {
  return <main className="page simple-page help-page"><p className="eyebrow">Helpful, not preachy</p><h1>Using Family Meals</h1>
    <article><h2>Keep logging easy</h2><p>Quick add logs the usual serving to the meal shown on its card. Tap a meal entry to change its meal or quantity, and use Undo when you make a mistake.</p></article>
    <article id="points-guide"><h2>Daily, Weekly, and personal Fuel Points</h2><p><strong>Daily Points</strong> are your food target for today. <strong>Weeklies</strong> are a flexible pool that covers daily overages and may include limited rollover.</p><p>With a nutrition plan, <strong>My Fuel Points</strong> applies the same transparent base formula to every food and gently emphasizes your selected priorities. A full day at a selected goal or limit changes the score by about 5 Points. Published Points remain visible as source detail but do not override your plan.</p><p>If nutrition required by your plan is missing, the score stays unknown rather than assuming zero. Plan changes begin the next day so one day never mixes scoring rules.</p><div className="help-source"><span>Program context</span><a href="https://www.weightwatchers.com/us/blog/weight-loss/points-glp-1-program-explained" target="_blank" rel="noreferrer">WeightWatchers: Points in the GLP-1 program</a><small>Published April 25, 2025 · checked August 12, 2026</small></div></article>
    <article><h2>Choosing nutrition priorities</h2><p>Presets are plain-language tracking tools, not diagnoses. The saturated-fat and fiber plan emphasizes those Nutrition Facts values. Lower sodium uses an editable daily limit. Protein through the day tracks both a daily goal and breakfast, lunch, and dinner minimums.</p><p>Nutrition labels generally report total dietary fiber, not soluble fiber. Family Meals therefore tracks total fiber and does not claim to measure soluble fiber or particular foods such as oats, beans, or barley.</p><div className="help-sources"><div className="help-source"><span>Heart-health pattern</span><a href="https://professional.heart.org/en/science-news/-/media/31C986CF6C02449DB2C3A947BA45C271.ashx" target="_blank" rel="noreferrer">AHA: 2026 dyslipidemia patient messages</a><small>Published March 2026 · checked August 17, 2026</small></div><div className="help-source"><span>Cholesterol nutrition</span><a href="https://www.nhlbi.nih.gov/health/TLC-Therapeutic-Lifestyle-Changes-Lower-Cholesterol" target="_blank" rel="noreferrer">NHLBI: Therapeutic Lifestyle Changes</a><small>Checked August 17, 2026</small></div><div className="help-source"><span>Sodium references</span><a href="https://www.nhlbi.nih.gov/health/dash/following-dash" target="_blank" rel="noreferrer">NHLBI: Following DASH</a><small>Checked August 17, 2026</small></div><div className="help-source"><span>Label reference</span><a href="https://www.fda.gov/food/nutrition-facts-label/daily-value-nutrition-and-supplement-facts-labels" target="_blank" rel="noreferrer">FDA: Daily Values</a><small>Checked August 17, 2026</small></div></div><p>Defaults are editable general references. Use targets or limits supplied by your clinician when applicable.</p></article>
    <article><h2>Protein, fiber, and water</h2><p>These remain visible nutrition signals, not bonus Points. Protein and fiber come from logged foods. Water includes food nutrition and anything you add from the Water card.</p><div className="help-sources"><div className="help-source"><span>Nutrition guidance</span><a href="https://www.weightwatchers.com/us/blog/weight-loss/best-diet-to-follow-while-using-glp-1-meds" target="_blank" rel="noreferrer">WeightWatchers: Food choices while using a GLP-1</a><small>Published June 22, 2026 · checked August 12, 2026</small></div><div className="help-source"><span>Tracking context</span><a href="https://www.weightwatchers.com/us/blog/app-features/weightwatchers-macronutrients" target="_blank" rel="noreferrer">WeightWatchers: Macronutrient tracking</a><small>Published February 4, 2025 · checked August 12, 2026</small></div></div></article>
    <article><h2>Workout days</h2><p>Exercise is tracked separately and never automatically adds food Points. After a vigorous or 60-minute session, Family Meals reminds you to meet your usual protein target and pay attention to fluids and recovery. Wearable calorie estimates are not treated as food credit.</p></article>
    <article><h2>Source quality</h2><p>Official restaurant nutrition is labeled official. User-entered facts say so. If a restaurant does not publish a number, Family Meals leaves it unknown instead of inventing one.</p></article>
    <article><h2>Medication information</h2><p>Your dose is private context for you and your family; Family Meals does not recommend or change it. Use the current prescribing information and your clinician for dosing and safety decisions.</p><div className="help-source"><span>Official labeling</span><a href="https://www.novo-pi.com/wegovy.pdf" target="_blank" rel="noreferrer">Wegovy prescribing information</a><small>Revised June 2026 · checked August 10, 2026</small></div></article>
    <article><h2>Privacy</h2><p>Your profile, medication context, targets, meals, and progress are private. Family members share only the catalog and recipes.</p></article>
    <p className="source-review">Source review: August 17, 2026. Public pages can change; linked dates make later review possible.</p>
    <p className="disclaimer">Family Meals is not affiliated with WeightWatchers or Novo Nordisk. Its recommendations and Fuel Points are Family Meals estimates, not medical advice.</p>
  </main>;
}

function Settings({ profile, onSaved, onLogout }: { profile: Profile; onSaved: () => void; onLogout: () => void }) {
  const existing = profile.scheduled_nutrition_plan ?? profile.active_nutrition_plan;
  const initialPreset = existing?.preset_key ?? 'balanced';
  const initial = existing ? {
    priorities: existing.priorities, proteinTargetG: Number(existing.protein_target_g), fiberTargetG: Number(existing.fiber_target_g),
    waterTargetMl: Number(existing.water_target_ml), saturatedFatLimitG: Number(existing.saturated_fat_limit_g),
    addedSugarLimitG: Number(existing.added_sugar_limit_g), sodiumLimitMg: Number(existing.sodium_limit_mg),
    proteinPerMealG: existing.protein_per_meal_g == null ? null : Number(existing.protein_per_meal_g),
    dailyPoints: Number(existing.daily_points_budget), weeklyPoints: Number(existing.weekly_points_budget),
  } : nutritionPlanDefaults(profile, 'balanced');
  const [preset, setPreset] = useState<NutritionPreset>(initialPreset);
  const [priorities, setPriorities] = useState<NutritionPriority[]>(initial.priorities);
  const [protein, setProtein] = useState(formatDecimalInput(initial.proteinTargetG, 1));
  const [fiber, setFiber] = useState(formatDecimalInput(initial.fiberTargetG, 1));
  const [water] = useState(initial.waterTargetMl);
  const [satFat, setSatFat] = useState(formatDecimalInput(initial.saturatedFatLimitG, 1));
  const [addedSugar, setAddedSugar] = useState(formatDecimalInput(initial.addedSugarLimitG, 1));
  const [sodium, setSodium] = useState(formatDecimalInput(initial.sodiumLimitMg, 0));
  const [perMeal, setPerMeal] = useState(formatDecimalInput(initial.proteinPerMealG, 1));
  const [budgetMode, setBudgetMode] = useState<BudgetMode>(existing?.budget_source === 'user_entered' ? 'custom' : 'recommended');
  const [daily, setDaily] = useState(String(initial.dailyPoints));
  const [weekly, setWeekly] = useState(String(initial.weeklyPoints));
  const [dose, setDose] = useState(formatDecimalInput(profile.medication_dose_mg));
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const recommendation = suggestedBudget({
    weightKg: profile.current_weight_kg == null ? null : Number(profile.current_weight_kg),
    heightCm: profile.height_cm == null ? null : Number(profile.height_cm), ageYears: profile.age_years == null ? null : Number(profile.age_years),
    sex: profile.sex_for_energy ?? 'unspecified', baselineActivity: profile.baseline_activity ?? 'mostly_seated',
    proteinG: Number(protein), fiberG: Number(fiber), priorities,
    saturatedFatLimitG: Number(satFat), addedSugarLimitG: Number(addedSugar), sodiumLimitMg: Number(sodium),
  });

  function choosePreset(next: NutritionPreset) {
    const defaults = nutritionPlanDefaults(profile, next);
    setPreset(next); setPriorities(defaults.priorities); setProtein(formatDecimalInput(defaults.proteinTargetG, 1));
    setFiber(formatDecimalInput(defaults.fiberTargetG, 1)); setSatFat(formatDecimalInput(defaults.saturatedFatLimitG, 1));
    setAddedSugar(formatDecimalInput(defaults.addedSugarLimitG, 1)); setSodium(String(defaults.sodiumLimitMg));
    setPerMeal(formatDecimalInput(defaults.proteinPerMealG, 1)); setDaily(String(defaults.dailyPoints)); setWeekly(String(defaults.weeklyPoints));
    setMessage(''); setError('');
  }

  function togglePriority(priority: NutritionPriority) {
    setPriorities((current) => current.includes(priority) ? current.filter((item) => item !== priority)
      : current.length < 3 ? [...current, priority] : current);
  }

  function chooseBudgetMode(mode: BudgetMode) {
    setBudgetMode(mode); setMessage(''); setError('');
    if (mode === 'recommended' && recommendation) { setDaily(String(recommendation.dailyPoints)); setWeekly(String(recommendation.weeklyPoints)); }
  }

  async function submit(event: FormEvent) {
    event.preventDefault(); setMessage(''); setError('');
    const numericTargets = [protein,fiber,satFat,addedSugar,sodium].map(asNumber);
    if (numericTargets.some((value) => value == null || value <= 0) || (priorities.includes('protein') && (!asNumber(perMeal) || Number(perMeal) <= 0))) {
      setError('Enter positive numbers for each goal and limit.'); return;
    }
    if (preset === 'custom' && (priorities.length < 1 || priorities.length > 3)) { setError('Choose one to three priorities.'); return; }
    const dailyValue = budgetMode === 'recommended' ? recommendation?.dailyPoints ?? null : asNumber(daily);
    const weeklyValue = budgetMode === 'recommended' ? recommendation?.weeklyPoints ?? null : asNumber(weekly);
    if (budgetMode === 'recommended' && !recommendation) {
      setError('Add weight, height, and age to your profile before using recommended guidance.'); return;
    }
    if (dailyValue === null || weeklyValue === null || !Number.isInteger(dailyValue) || !Number.isInteger(weeklyValue) || dailyValue < 0 || weeklyValue < 0) {
      setError('Enter whole numbers for both Daily and Weekly custom Points.'); return;
    }
    try {
      await api('/api/v1/profile', { method: 'PUT', body: JSON.stringify({
        medication_dose_mg: asNumber(dose),
      }) });
      await api('/api/v1/nutrition-plan', { method: 'PUT', body: JSON.stringify({
        preset_key: preset, priorities, protein_target_g: Number(protein), fiber_target_g: Number(fiber), water_target_ml: water,
        saturated_fat_limit_g: Number(satFat), added_sugar_limit_g: Number(addedSugar), sodium_limit_mg: Number(sodium),
        protein_per_meal_g: priorities.includes('protein') ? Number(perMeal) : null,
        daily_points_budget: dailyValue, weekly_points_budget: weeklyValue,
        budget_source: budgetMode === 'recommended' ? 'family_suggestion_v2' : 'user_entered',
      }) });
      setDaily(String(dailyValue)); setWeekly(String(weeklyValue));
      setMessage(`Your plan is scheduled for tomorrow with ${dailyValue} Daily and ${weeklyValue} Weekly Points.`);
      onSaved();
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Could not save'); }
  }
  const presetLabels: Record<NutritionPreset, [string,string]> = {
    balanced: ['Balanced','Protein, fiber, and everyday food quality'],
    saturated_fat_fiber: ['Saturated fat + fiber','Less saturated fat and more total fiber'],
    lower_sodium: ['Lower sodium','Make sodium count more in your score'],
    protein_through_day: ['Protein through the day','Daily protein plus breakfast, lunch, and dinner'],
    custom: ['Customize','Choose up to three nutrition priorities'],
  };
  const priorityLabels: Record<NutritionPriority,string> = { protein:'More protein',fiber:'More fiber',saturated_fat:'Less saturated fat',added_sugar:'Less added sugar',sodium:'Less sodium' };
  async function cancelScheduled() {
    setError(''); await api('/api/v1/nutrition-plan/scheduled', { method:'DELETE' }); setMessage('Scheduled change cancelled.'); onSaved();
  }
  return <main className="page simple-page"><p className="eyebrow">Private to you</p><h1>Settings</h1>
    {profile.active_nutrition_plan ? <p className="active-plan">Current plan: <strong>{presetLabels[profile.active_nutrition_plan.preset_key][0]}</strong></p> : <p className="active-plan">Current plan: <strong>Legacy Fuel Points</strong></p>}
    {profile.scheduled_nutrition_plan && <div className="scheduled-plan"><span>Starts {profile.scheduled_nutrition_plan.effective_from}</span><strong>{presetLabels[profile.scheduled_nutrition_plan.preset_key][0]}</strong><button type="button" onClick={() => void cancelScheduled()}>Cancel scheduled change</button></div>}
    <form className="form-stack settings-form" onSubmit={submit}>
    <fieldset><legend>Nutrition plan</legend><div className="plan-presets">{(Object.keys(presetLabels) as NutritionPreset[]).map((key) => <button type="button" key={key} className={preset === key ? 'selected' : ''} onClick={() => choosePreset(key)}><strong>{presetLabels[key][0]}</strong><small>{presetLabels[key][1]}</small></button>)}</div></fieldset>
    {preset === 'custom' && <fieldset><legend>Choose one to three priorities</legend><div className="priority-choices">{(Object.keys(priorityLabels) as NutritionPriority[]).map((key) => <button type="button" key={key} className={priorities.includes(key) ? 'selected' : ''} disabled={!priorities.includes(key) && priorities.length >= 3} onClick={() => togglePriority(key)}>{priorityLabels[key]}</button>)}</div></fieldset>}
    <fieldset className="target-editor"><legend>Your goals and limits</legend><div className="two-fields">
      <label>Protein goal <span><input inputMode="decimal" min="0.1" value={protein} onChange={(e) => setProtein(e.target.value)} /> g/day</span></label>
      <label>Fiber goal <span><input inputMode="decimal" min="0.1" value={fiber} onChange={(e) => setFiber(e.target.value)} /> g/day</span></label>
      {priorities.includes('saturated_fat') && <label>Saturated fat limit <span><input inputMode="decimal" min="0.1" value={satFat} onChange={(e) => setSatFat(e.target.value)} /> g/day</span></label>}
      {priorities.includes('added_sugar') && <label>Added sugar limit <span><input inputMode="decimal" min="0.1" value={addedSugar} onChange={(e) => setAddedSugar(e.target.value)} /> g/day</span></label>}
      {priorities.includes('sodium') && <label>Sodium limit <span><input inputMode="numeric" min="1" value={sodium} onChange={(e) => setSodium(e.target.value)} /> mg/day</span><span className="inline-choices"><button type="button" onClick={() => setSodium('2300')}>2,300</button><button type="button" onClick={() => setSodium('1500')}>1,500</button></span></label>}
      {priorities.includes('protein') && <label>Protein at each main meal <span><input inputMode="decimal" min="0.1" value={perMeal} onChange={(e) => setPerMeal(e.target.value)} /> g</span></label>}
    </div><small>These editable defaults are general references, not prescriptions. Use numbers from your clinician when provided.</small></fieldset>
    <fieldset><legend>Points guidance</legend><div className="budget-mode" role="group" aria-label="Points guidance source">
      <button type="button" className={budgetMode === 'recommended' ? 'selected' : ''} onClick={() => chooseBudgetMode('recommended')} disabled={!recommendation}>Recommended</button>
      <button type="button" className={budgetMode === 'custom' ? 'selected' : ''} onClick={() => chooseBudgetMode('custom')}>Custom</button>
    </div></fieldset>
    {budgetMode === 'recommended' ? <div className="budget-guidance">
      <span>Family Meals recommendation</span>
      <strong>{recommendation ? `${recommendation.dailyPoints} Daily · ${recommendation.weeklyPoints} Weekly Points` : 'Profile details needed'}</strong>
      <small>Provisional starting guidance calculated with this nutrition plan. We will calibrate it against complete, realistic days before changing the recommendation.</small>
    </div> : <div className="custom-budget">
      <div className="two-fields"><label>Daily Points<small>Your daily food allowance.</small><input inputMode="numeric" min="0" step="1" value={daily} onChange={(e) => setDaily(e.target.value)} required /></label><label>Weekly Points<small>Flexible Points for the week.</small><input inputMode="numeric" min="0" step="1" value={weekly} onChange={(e) => setWeekly(e.target.value)} required /></label></div>
      {recommendation && <button type="button" className="text-button restore-guidance" onClick={() => chooseBudgetMode('recommended')}>Restore Family Meals recommendation</button>}
    </div>}
    <label>Current Wegovy dose <small>Optional</small><span><input inputMode="decimal" value={dose} onChange={(e) => setDose(e.target.value)} /> mg</span></label>
    {error && <p className="error" role="alert">{error}</p>}
    <button className="primary-button">Schedule this plan for tomorrow</button>
    {message && <p className="form-message" role="status">{message}</p>}
  </form><button className="text-button logout" onClick={onLogout}>Sign out and clear offline data</button></main>;
}

export function App() {
  const queryClient = useQueryClient();
  const [view, setView] = useState<View>('today');
  const [addOpen, setAddOpen] = useState(false);
  const [addSlot, setAddSlot] = useState<MealSlot>(defaultSlot());
  const [activityOpen, setActivityOpen] = useState(false);
  const [waterOpen, setWaterOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<MealEntry | null>(null);
  const [editingFood, setEditingFood] = useState<FoodServing | null>(null);
  const [infoTopic, setInfoTopic] = useState<InfoTopic | null>(null);
  const [quickBusy, setQuickBusy] = useState('');
  const [toast, setToast] = useState<ToastNotice | null>(null);
  const session = useQuery({ queryKey: ['session'], queryFn: loadSession, staleTime: Infinity });
  const profile = useQuery({ queryKey: ['profile'], queryFn: () => api<Profile>('/api/v1/profile'), enabled: session.data?.authenticated === true });
  const dashboard = useQuery({ queryKey: ['dashboard', localDate()], queryFn: () => api<DashboardData>(`/api/v1/dashboard?date=${localDate()}`), enabled: profile.data?.onboarding_complete === true });
  const day = useQuery({ queryKey: ['day', localDate()], queryFn: () => api<{ entries: MealEntry[] }>(`/api/v1/day?date=${localDate()}`), enabled: profile.data?.onboarding_complete === true });
  const activities = useQuery({ queryKey: ['activities', localDate()], queryFn: () => api<{ entries: ActivityEntry[] }>(`/api/v1/activities?date=${localDate()}`), enabled: profile.data?.onboarding_complete === true });

  useEffect(() => {
    async function sync() {
      if (!session.data?.authenticated) return;
      for (const mutation of await pendingMutations()) {
        try { await api(mutation.path, { method: 'POST', body: JSON.stringify(mutation.body) }); await removeMutation(mutation.id); }
        catch { break; }
      }
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['day'] });
      void queryClient.invalidateQueries({ queryKey: ['activities'] });
    }
    window.addEventListener('online', sync);
    void sync();
    return () => window.removeEventListener('online', sync);
  }, [session.data?.authenticated, queryClient]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), toast.action ? 6000 : 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (session.isLoading) return <div className="splash"><div className="brand-mark">FM</div></div>;
  if (!session.data?.authenticated) return <Login />;
  if (profile.isLoading || !profile.data) return <div className="splash"><div className="brand-mark">FM</div><p>Loading your private profile…</p></div>;
  if (!profile.data.onboarding_complete) return <Onboarding profile={profile.data} onDone={() => void profile.refetch()} />;

  function showToast(message: string, action?: () => void | Promise<void>) {
    setToast({ message, actionLabel: action ? 'Undo' : undefined, action });
  }
  async function refreshMeals() {
    await Promise.all([dashboard.refetch(), day.refetch()]);
  }
  async function undoCreatedEntry(entry: MealEntry) {
    await api(`/api/v1/entries/${entry.id}`, { method: 'DELETE' });
    await refreshMeals();
    showToast('Add undone');
  }
  function handleFoodLogged(result: FoodLogResult) {
    if (result.entry) {
      showToast(result.message, () => undoCreatedEntry(result.entry!));
    } else if (result.queuedMutationID) {
      showToast(result.message, async () => { await removeMutation(result.queuedMutationID!); showToast('Offline add undone'); });
    } else showToast(result.message);
  }
  async function quickAdd(item: RecentFood) {
    const mutationID = crypto.randomUUID();
    const body = {
      serving_id: item.serving_id,
      client_mutation_id: mutationID,
      eaten_at: new Date().toISOString(),
      local_date: localDate(),
      slot: item.slot,
      quantity: Number(item.quantity) || 1,
    };
    setQuickBusy(item.serving_id);
    try {
      const entry = await api<MealEntry>('/api/v1/entries', { method: 'POST', body: JSON.stringify(body) });
      await refreshMeals();
      showToast(`${item.snapshot.name} added to ${slotLabels[item.slot]}`, () => undoCreatedEntry(entry));
    } catch (reason) {
      const networkFailure = !navigator.onLine || reason instanceof TypeError || String(reason).includes('Failed to fetch');
      if (networkFailure) {
        await queueMutation({ id: mutationID, path: '/api/v1/entries', body, queuedAt: new Date().toISOString() });
        showToast(`${item.snapshot.name} saved offline for ${slotLabels[item.slot]}`, async () => { await removeMutation(mutationID); showToast('Offline add undone'); });
      } else showToast(reason instanceof Error ? reason.message : 'Could not add food');
    } finally { setQuickBusy(''); }
  }
  async function removeEntry(entry: MealEntry) {
    try {
      await api(`/api/v1/entries/${entry.id}`, { method: 'DELETE' });
      setEditingEntry(null); await refreshMeals();
      showToast(`${entry.snapshot.name} removed`, async () => {
        await api(`/api/v1/entries/${entry.id}/restore`, { method: 'POST' });
        await refreshMeals(); showToast(`${entry.snapshot.name} restored`);
      });
    } catch (reason) { showToast(reason instanceof Error ? reason.message : 'Could not remove entry'); }
  }
  async function updateEntry(entry: MealEntry, slot: MealSlot, quantity: number) {
    if (entry.slot === slot && Number(entry.quantity) === quantity) { setEditingEntry(null); showToast('No changes needed'); return; }
    await api<MealEntry>(`/api/v1/entries/${entry.id}`, { method: 'PUT', body: JSON.stringify({ slot, quantity }) });
    setEditingEntry(null); await refreshMeals();
    const message = entry.slot === slot ? `${entry.snapshot.name} quantity updated` : `${entry.snapshot.name} moved to ${slotLabels[slot]}`;
    showToast(message, async () => {
      await api(`/api/v1/entries/${entry.id}`, { method: 'PUT', body: JSON.stringify({ slot: entry.slot, quantity: entry.quantity }) });
      await refreshMeals(); showToast('Change undone');
    });
  }
  async function removeActivity(id: string) {
    try { await api(`/api/v1/activities/${id}`, { method: 'DELETE' }); showToast('Activity removed'); await dashboard.refetch(); await activities.refetch(); }
    catch (reason) { showToast(reason instanceof Error ? reason.message : 'Could not remove activity'); }
  }
  async function logWater(amountML: number) {
    const mutationID = crypto.randomUUID();
    const body = { client_mutation_id: mutationID, logged_at: new Date().toISOString(), local_date: localDate(), amount_ml: amountML };
    const ounces = formatDecimalInput(amountML / 29.5735, 1);
    try {
      const entry = await api<WaterEntry>('/api/v1/water', { method: 'POST', body: JSON.stringify(body) });
      setWaterOpen(false); await dashboard.refetch();
      showToast(`${ounces} fl oz water logged`, async () => {
        await api(`/api/v1/water/${entry.id}`, { method: 'DELETE' });
        await dashboard.refetch(); showToast('Water add undone');
      });
    } catch (reason) {
      const networkFailure = !navigator.onLine || reason instanceof TypeError || String(reason).includes('Failed to fetch');
      if (!networkFailure) throw reason;
      await queueMutation({ id: mutationID, path: '/api/v1/water', body, queuedAt: new Date().toISOString() });
      setWaterOpen(false);
      showToast(`${ounces} fl oz water saved offline`, async () => { await removeMutation(mutationID); showToast('Offline water add undone'); });
    }
  }
  async function logout() {
    await purgePrivateStorage();
    await api('/auth/logout', { method: 'POST' });
    queryClient.clear();
    window.location.assign('/');
  }

  function openFood(slot?: MealSlot) {
    setToast(null);
    setAddSlot(slot ?? defaultSlot());
    setAddOpen(true);
  }
  function openActivity() {
    setToast(null);
    setActivityOpen(true);
  }
  function openWater() {
    setToast(null);
    setWaterOpen(true);
  }
  function changeView(nextView: View) {
    setToast(null);
    setEditingEntry(null);
    setEditingFood(null);
    setInfoTopic(null);
    setView(nextView);
  }

  return <div className={`app-shell ${view === 'today' ? 'today-active' : ''}`}>
    {view === 'today' && <Today dashboard={dashboard.data} entries={day.data?.entries} activities={activities.data?.entries} onAdd={openFood} onQuickAdd={(item) => void quickAdd(item)} quickBusy={quickBusy} onEdit={(entry) => { setToast(null); setEditingEntry(entry); }} onDelete={(entry) => void removeEntry(entry)} onAddWater={openWater} onAddActivity={openActivity} onDeleteActivity={removeActivity} onExplain={setInfoTopic} loading={dashboard.isLoading || day.isLoading || activities.isLoading} />}
    {view === 'foods' && <FoodCatalog onCreate={() => openFood()} onLog={(food) => quickAdd({ serving_id: food.serving_id, snapshot: food, quantity: 1, slot: defaultSlot(), favorite: Boolean(food.favorite) })} onEdit={(food) => { setToast(null); setEditingFood(food); }} onExplain={setInfoTopic} />}
    {view === 'help' && <Help />}
    {view === 'settings' && <Settings profile={profile.data} onSaved={() => void Promise.all([profile.refetch(), dashboard.refetch()])} onLogout={logout} />}
    <nav className="bottom-nav" aria-label="Primary">
      {([['today', '◉', 'Today'], ['foods', '⌕', 'Foods'], ['help', '?', 'Help'], ['settings', '⚙', 'Settings']] as const).map(([key, icon, label]) => <button key={key} className={view === key ? 'active' : ''} onClick={() => changeView(key)}><span>{icon}</span>{label}</button>)}
    </nav>
    {addOpen && <AddSheet dashboard={dashboard.data} initialSlot={addSlot} onClose={() => setAddOpen(false)} onLogged={handleFoodLogged} />}
    {waterOpen && <WaterSheet onClose={() => setWaterOpen(false)} onSave={logWater} />}
    {activityOpen && <ActivitySheet onClose={() => setActivityOpen(false)} onLogged={(message) => { showToast(message); void dashboard.refetch(); void activities.refetch(); }} />}
    {editingEntry && <EntryEditor entry={editingEntry} onClose={() => setEditingEntry(null)} onSave={(slot, quantity) => updateEntry(editingEntry, slot, quantity)} onRemove={() => removeEntry(editingEntry)} />}
    {editingFood && <FoodEditor food={editingFood} onClose={() => setEditingFood(null)} onSaved={(food) => { setEditingFood(null); void queryClient.invalidateQueries({ queryKey: ['food-catalog'] }); void queryClient.invalidateQueries({ queryKey: ['foods'] }); showToast(`${food.name} updated`); }} />}
    {infoTopic && <InfoSheet topic={infoTopic} onClose={() => setInfoTopic(null)} onReadMore={() => { setInfoTopic(null); setView('help'); }} />}
    {toast && <div className="toast" role="status"><span>{toast.message}</span>{toast.action && <button onClick={() => { const action = toast.action!; setToast(null); void Promise.resolve(action()).catch((reason) => showToast(reason instanceof Error ? reason.message : 'Could not undo')); }}>{toast.actionLabel}</button>}</div>}
  </div>;
}
